import { useEffect, useState, useMemo } from "react";
import { motion } from "framer-motion";
import { api, CATEGORY_META } from "../lib/api";
import { Input } from "../components/ui/input";
import { SeverityBadge } from "../components/SeverityBadge";
import { IssueDetailDialog } from "../components/IssueDetailDialog";
import { BookText, Search, ChevronRight } from "lucide-react";

const KnowledgeBase = () => {
  const [issues, setIssues] = useState([]);
  const [categories, setCategories] = useState([]);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("All");
  const [selected, setSelected] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    api.get("/issues").then((r) => {
      setIssues(r.data.issues);
      setCategories(["All", ...r.data.categories]);
    }).catch(() => {});
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return issues.filter((i) => {
      const matchCat = filter === "All" || i.category === filter;
      const matchQ = !q ||
        i.title.toLowerCase().includes(q) ||
        i.error_code.toLowerCase().includes(q) ||
        i.component.toLowerCase().includes(q);
      return matchCat && matchQ;
    });
  }, [issues, query, filter]);

  const openIssue = (i) => { setSelected(i); setOpen(true); };

  return (
    <div className="max-w-6xl mx-auto px-5 sm:px-6 py-10">
      <div className="flex items-center gap-3 mb-6">
        <div className="h-11 w-11 rounded-xl bg-primary/12 border border-primary/25 grid place-items-center">
          <BookText className="h-5 w-5 text-primary" />
        </div>
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">IT Knowledge Base</h1>
          <p className="text-sm text-muted-foreground">Browse common Microsoft issues with root cause & one-click fixes.</p>
        </div>
      </div>

      {/* controls */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input value={query} onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by title, error code or component…"
            className="bg-secondary/40 border-border/70 h-12 rounded-xl pl-10" data-testid="kb-search-input" />
        </div>
      </div>
      <div className="flex flex-wrap gap-2 mb-7">
        {categories.map((c) => (
          <button key={c} onClick={() => setFilter(c)}
            className={`text-sm px-4 py-2 rounded-full border transition-all ${
              filter === c ? "border-primary/60 bg-primary/10 text-primary" : "border-border/70 bg-secondary/40 hover:border-primary/40"}`}
            data-testid={`kb-filter-${c.replace(/[^a-z]/gi, "").toLowerCase()}`}>
            {c}
          </button>
        ))}
      </div>

      {/* grid */}
      <div className="grid sm:grid-cols-2 gap-4">
        {filtered.map((issue, idx) => {
          const meta = CATEGORY_META[issue.category] || {};
          return (
            <motion.button key={issue.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: Math.min(idx * 0.04, 0.4) }} onClick={() => openIssue(issue)}
              className="glass rounded-2xl p-5 text-left hover:-translate-y-0.5 hover:border-primary/40 transition-all duration-200"
              data-testid={`kb-issue-${issue.id}`}>
              <div className="flex items-center justify-between mb-3">
                <span className="text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-full border"
                  style={{ color: meta.color, borderColor: `${meta.color}55`, background: `${meta.color}14` }}>
                  {issue.category}
                </span>
                <SeverityBadge severity={issue.severity} />
              </div>
              <h3 className="font-heading font-semibold text-base leading-snug">{issue.title}</h3>
              <p className="text-xs font-mono text-muted-foreground mt-2">{issue.error_code} · {issue.component}</p>
              <p className="text-sm text-muted-foreground mt-3 line-clamp-2 leading-relaxed">{issue.root_cause}</p>
              <span className="inline-flex items-center gap-1 text-sm font-semibold text-primary mt-4">
                View fix <ChevronRight className="h-4 w-4" />
              </span>
            </motion.button>
          );
        })}
      </div>

      {filtered.length === 0 && (
        <div className="text-center py-20 text-muted-foreground" data-testid="kb-empty">
          No issues match your search.
        </div>
      )}

      <IssueDetailDialog issue={selected} open={open} onOpenChange={setOpen} />
    </div>
  );
};

export default KnowledgeBase;
